import dynamic from "next/dynamic"
import Link from "next/link"
import { MDXRemote, type MDXRemoteSerializeResult } from "next-mdx-remote"
import { type ReactNode, useState } from "react"
import { reactText } from "utils/reactText"
import { slugify } from "utils/slugify"
import type * as EngineeringDiagrams from "@/components/resources/components/EngineeringDiagrams"
import { markdownEmbedComponents } from "@/components/resources/components/MarkdownEmbeds"

const DIAGRAMS = [
  "AssetPipelineDiagram",
  "CapabilityDetail",
  "CapabilityGraph",
  "ClassifyTrace",
  "CoverageDashboard",
  "DataCorrectnessHero",
  "DenylistTable",
  "DerivativeTokenTable",
  "InteractivePriceCurves",
  "InvestigateFix",
  "KnowledgeGraph",
  "LpTokenTable",
  "PrincipleDocCard",
  "PriorityList",
  "SkillCard",
  "SpikeRejection",
  "TickChart",
  "TradingFilterScatter",
  "TrustHierarchy",
] as const satisfies readonly (keyof typeof EngineeringDiagrams)[]

// The engineering diagrams are about 2,300 lines. Loaded on demand, so a post without one does not download them.
const engineeringDiagrams = Object.fromEntries(
  DIAGRAMS.map((name) => [
    name,
    dynamic(() =>
      import("@/components/resources/components/EngineeringDiagrams").then(
        (m) => m[name]
      )
    ),
  ])
)

/**
 * Click-to-zoom image. Click to view fullscreen, click again to close.
 * The Markdown title (`![alt](src "caption")`) shows as a caption. Only phrasing
 * elements are used, so it stays valid inside the <p> MDX wraps an image in.
 */
const ClickToZoomImage = ({
  src,
  alt,
  title,
  width,
  height,
}: {
  src?: string
  alt?: string
  title?: string
  width?: number | string
  height?: number | string
}) => {
  const [enlarged, setEnlarged] = useState(false)

  if (!src) return null

  return (
    <>
      <img
        src={src}
        alt={alt || ""}
        width={width}
        height={height}
        loading="lazy"
        decoding="async"
        className={
          enlarged
            ? "fixed inset-0 z-50 w-screen h-screen object-contain bg-black/90 p-8 cursor-zoom-out"
            : "mb-4 md:mb-6 rounded-lg cursor-zoom-in w-full h-auto"
        }
        onClick={() => setEnlarged(!enlarged)}
      />
      {title && (
        <span className="block -mt-2 md:-mt-4 mb-4 md:mb-6 px-3 text-sm text-fg-secondary text-center">
          {title}
        </span>
      )}
    </>
  )
}

/** Lightweight syntax highlighter for SQL and Solidity-like code blocks. No external deps. */
const highlightCode = (code: string, lang: string): string => {
  const esc = (s: string) =>
    s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")

  if (lang === "sql") {
    const sqlKeywords =
      /\b(select|from|where|group\s+by|order\s+by|inner\s+join|left\s+join|on|as|and|or|not|in|is|null|union\s+all|with|insert|into|values|create|table|drop|alter|having|limit|offset|between|like|case|when|then|else|end|distinct|exists|cast|interval)\b/gi
    return code
      .split("\n")
      .map((line) => {
        const commentMatch = line.match(/^(\s*)(--.*)/)
        if (commentMatch)
          return `${esc(commentMatch[1])}<span class="text-emerald-600 dark:text-emerald-400/70">${esc(commentMatch[2])}</span>`
        return line
          .replace(
            /('(?:[^'\\]|\\.)*')/g,
            (_, s) =>
              `<span class="text-amber-500 dark:text-amber-400">${esc(s)}</span>`
          )
          .replace(
            sqlKeywords,
            (kw) => `<span class="text-blue-400">${esc(kw)}</span>`
          )
          .replace(
            /\b(sum|count|date|pow|timestamp_trunc|greatest|cast)\b/gi,
            (fn) => `<span class="text-yellow-300">${esc(fn)}</span>`
          )
          .replace(/(?<=^|\n)(\s*(?!<span)[a-z_]+(?:\.[a-z_]+)*)/g, (id) =>
            esc(id)
          )
      })
      .join("\n")
  }

  if (lang === "yaml" || lang === "yml") {
    return code
      .split("\n")
      .map((line) => {
        // Full-line comment
        const commentMatch = line.match(/^(\s*)(#.*)/)
        if (commentMatch)
          return `${esc(commentMatch[1])}<span class="text-emerald-600 dark:text-emerald-400/70">${esc(commentMatch[2])}</span>`
        // "key: value" pattern (value may be empty for nested blocks)
        const kvMatch = line.match(/^(\s*)([A-Za-z_][\w-]*)(:)(\s*)(.*)$/)
        if (!kvMatch) return esc(line)
        const [, indent, key, colon, space, rest] = kvMatch
        const keyHtml = `<span class="text-blue-400">${esc(key)}</span>${esc(colon)}${esc(space)}`
        if (!rest) return `${esc(indent)}${keyHtml}`
        // Split off any inline trailing comment
        const inlineCommentMatch = rest.match(/^(.*?)(\s+#.*)$/)
        const valueStr = inlineCommentMatch ? inlineCommentMatch[1] : rest
        const commentStr = inlineCommentMatch ? inlineCommentMatch[2] : ""
        let valueHtml: string
        if (/^".*"$|^'.*'$/.test(valueStr)) {
          valueHtml = `<span class="text-amber-500 dark:text-amber-400">${esc(valueStr)}</span>`
        } else if (/^(true|false|null|~)$/i.test(valueStr)) {
          valueHtml = `<span class="text-purple-400">${esc(valueStr)}</span>`
        } else if (/^-?\d+(\.\d+)?$/.test(valueStr)) {
          valueHtml = `<span class="text-purple-400">${esc(valueStr)}</span>`
        } else {
          valueHtml = esc(valueStr)
        }
        const commentHtml = commentStr
          ? `<span class="text-emerald-600 dark:text-emerald-400/70">${esc(commentStr)}</span>`
          : ""
        return `${esc(indent)}${keyHtml}${valueHtml}${commentHtml}`
      })
      .join("\n")
  }

  if (lang === "diagram") {
    // Highlight canonical metric names in green; preserve everything else (box-drawing chars, spaces).
    const metricNames = [
      "Outstanding supply",
      "Supply-side fees",
      "Trading volume",
      "Transfer volume",
      "Active loans",
      "Market cap",
      "TVL",
      "Revenue",
      "Fees",
      "APY",
      "Volume",
      "Holders",
    ]
    // Build a single regex; longest first so multi-word matches win over single-word substrings.
    const pattern = new RegExp(
      `(${metricNames.map((n) => n.replace(/[-/\\^$*+?.()|[\]{}]/g, "\\$&")).join("|")})`,
      "g"
    )
    return esc(code).replace(
      pattern,
      (m) => `<span class="text-emerald-600 dark:text-emerald-400">${m}</span>`
    )
  }

  if (lang === "entity-diagram") {
    // Entity taxonomy diagram: entity names in green, metric names in white bold.
    // Order: longer phrases first so multi-word matches win over single-word substrings.
    const entityNames = [
      "Market sector",
      "Project",
      "Chain",
      "Product",
      "Asset",
    ]
    const metricNames = [
      "Outstanding supply",
      "Transfer volume",
      "Active loans",
      "Market cap",
      "TVL",
      "GMV",
      "Revenue",
      "Fees",
      "APY",
      "Senders",
    ]
    let result = esc(code)
    // Color entities green. Skip "Project" / "Asset" when followed by " metrics" (box headers).
    for (const name of entityNames) {
      const escaped = name.replace(/[-/\\^$*+?.()|[\]{}]/g, "\\$&")
      const lookahead =
        name === "Project" || name === "Asset" ? "(?!\\s+metrics)" : ""
      const pattern = new RegExp(`\\b${escaped}\\b${lookahead}`, "g")
      result = result.replace(
        pattern,
        (m) =>
          `<span class="text-emerald-600 dark:text-emerald-400">${m}</span>`
      )
    }
    // Color metric names white + medium weight.
    for (const name of metricNames) {
      const escaped = name.replace(/[-/\\^$*+?.()|[\]{}]/g, "\\$&")
      const pattern = new RegExp(`\\b${escaped}\\b`, "g")
      result = result.replace(
        pattern,
        (m) => `<span class="text-fg-default font-medium">${m}</span>`
      )
    }
    return result
  }

  // Solidity-like (event signatures, comments)
  return code
    .split("\n")
    .map((line) => {
      const commentMatch = line.match(/^(\s*)(\/\/.*)/)
      if (commentMatch)
        return `${esc(commentMatch[1])}<span class="text-emerald-600 dark:text-emerald-400/70">${esc(commentMatch[2])}</span>`
      return line
        .replace(
          /\b(event)\b/g,
          (kw) => `<span class="text-blue-400">${esc(kw)}</span>`
        )
        .replace(
          /\b(address|uint256|uint8|bool|string|bytes32)\b/g,
          (t) => `<span class="text-teal-400">${esc(t)}</span>`
        )
        .replace(
          /\b([A-Z][a-zA-Z]+)\(/g,
          (_, name) => `<span class="text-yellow-300">${esc(name)}</span>(`
        )
    })
    .join("\n")
}

/**
 * MDX components for the post body. Every category renders through them, so all posts look alike.
 */
const markdownComponents = {
  h1: ({ children }: { children: ReactNode }) => {
    const anchor = slugify(reactText(children))
    return (
      <h1
        id={anchor}
        className="font-medium text-2xl md:text-3xl mb-4 md:mb-6 pt-2 md:pt-4 [&_strong]:font-medium tracking-tight scroll-mt-[100px] in-[.changelog]:text-lg md:in-[.changelog]:text-xl"
        data-anchor={anchor}
      >
        {children}
      </h1>
    )
  },
  h2: ({ children }: { children: ReactNode }) => {
    const anchor = slugify(reactText(children))
    return (
      <h2
        id={anchor}
        className="font-medium text-2xl md:text-3xl mb-4 md:mb-6 pt-2 md:pt-4 [&_strong]:font-medium tracking-tight scroll-mt-[100px] in-[.changelog]:text-base md:in-[.changelog]:text-lg"
        data-anchor={anchor}
      >
        {children}
      </h2>
    )
  },
  h3: ({ children }: { children: ReactNode }) => {
    const anchor = slugify(reactText(children))
    return (
      <h3
        id={anchor}
        className="font-medium text-xl md:text-2xl mb-4 md:mb-6 pt-2 md:pt-4 [&_strong]:font-medium tracking-tight scroll-mt-[100px] in-[.changelog]:text-sm md:in-[.changelog]:text-base"
        data-anchor={anchor}
      >
        {children}
      </h3>
    )
  },
  p: ({ children }: { children: ReactNode }) => (
    <p className="mb-4 md:mb-6 text-base text-fg-secondary">{children}</p>
  ),
  a: ({ href, children }: { href?: string; children: ReactNode }) => {
    const className =
      "text-fg-emerald underline underline-offset-2 hover:opacity-80"
    // A plain anchor for external urls: next/link would rewrite "//" inside them (archive.org links)
    return href?.startsWith("http") || href?.startsWith("//") ? (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className={className}
      >
        {children}
      </a>
    ) : (
      <Link href={href || ""} className={className}>
        {children}
      </Link>
    )
  },
  strong: ({ children }: { children: ReactNode }) => (
    <strong className="font-medium text-fg-default">{children}</strong>
  ),
  em: ({ children }: { children: ReactNode }) => <em>{children}</em>,
  blockquote: ({ children }: { children: ReactNode }) => (
    <blockquote className="mb-4 md:mb-6 pt-1 md:pt-2 border-l-4 border-neutral-300 dark:border-neutral-600 pl-4 italic">
      {children}
    </blockquote>
  ),
  ul: ({ children }: { children: ReactNode }) => (
    <ul className="text-fg-secondary mb-4 md:mb-6 list-disc flex flex-col gap-3 [&_ul]:mb-0 md:[&_ul]:mb-0 [&_ol]:mb-0 md:[&_ol]:mb-0 [&_li>p]:mb-0 md:[&_li>p]:mb-0 [&_li>ul]:mt-4 md:[&_li>ul]:mt-4 [&_li>ol]:mt-4 md:[&_li>ol]:mt-4">
      {children}
    </ul>
  ),
  ol: ({ children }: { children: ReactNode }) => (
    <ol className="text-fg-secondary mb-4 md:mb-6 list-decimal flex flex-col gap-3 [&_ul]:mb-0 md:[&_ul]:mb-0 [&_ol]:mb-0 md:[&_ol]:mb-0 [&_li>p]:mb-0 md:[&_li>p]:mb-0 [&_li>ul]:mt-4 md:[&_li>ul]:mt-4 [&_li>ol]:mt-4 md:[&_li>ol]:mt-4">
      {children}
    </ol>
  ),
  li: ({ children }: { children: ReactNode }) => (
    <li className="mb-1 ml-4 pl-1">{children}</li>
  ),
  hr: () => <hr className="my-8 md:my-12 border-border-default" />,
  code: ({
    children,
    className,
  }: {
    children: ReactNode
    className?: string
  }) => {
    // Inline code (no language class)
    if (!className) {
      return (
        <code className="bg-bg-popover px-1.5 py-0.5 rounded-sm border border-border-default text-[13px] font-mono text-emerald-600 dark:text-emerald-400">
          {children}
        </code>
      )
    }
    // Code inside <pre> — apply syntax highlighting
    const raw = typeof children === "string" ? children : String(children)
    const lang = className?.replace("language-", "") || ""
    // ASCII diagrams keep terminal aesthetic — tight line-height so box-drawing
    // characters connect, left-aligned within the <pre> (no centering).
    const isDiagram = lang === "diagram" || lang === "entity-diagram"
    return (
      <code
        className={`font-mono ${isDiagram ? "text-xs leading-[1.15] tracking-tight" : "text-sm"}`}
        dangerouslySetInnerHTML={{ __html: highlightCode(raw, lang) }}
      />
    )
  },
  pre: ({ children }: { children: ReactNode }) => (
    <pre className="mb-4 md:mb-6 bg-bg-popover rounded-lg border border-border-default p-4 overflow-x-auto text-sm">
      {children}
    </pre>
  ),
  // Bordered table with wrapping cells, the look the posts had before the Markdown export
  table: ({ children }: { children: ReactNode }) => (
    <div className="mb-4 md:mb-6 rounded-tight border border-secondary max-w-full overflow-auto">
      <table className="w-full border-collapse">{children}</table>
    </div>
  ),
  thead: ({ children }: { children: ReactNode }) => (
    <thead className="bg-header">{children}</thead>
  ),
  tbody: ({ children }: { children: ReactNode }) => <tbody>{children}</tbody>,
  tr: ({ children }: { children: ReactNode }) => (
    <tr className="bg-inherit">{children}</tr>
  ),
  th: ({ children }: { children: ReactNode }) => (
    <th className="px-sm py-sm text-left text-xs font-medium text-secondary border-b border-secondary first:pl-s last:pr-s">
      {children}
    </th>
  ),
  td: ({ children }: { children: ReactNode }) => (
    <td className="px-sm py-sm text-left text-s text-secondary border-b border-secondary bg-primary first:pl-s last:pr-s [tr:last-child_&]:border-b-0">
      {children}
    </td>
  ),
  img: ClickToZoomImage,
  ...engineeringDiagrams,
  ...markdownEmbedComponents,
}

interface MarkdownContentRendererProps {
  source: MDXRemoteSerializeResult
}

/**
 * Renders the MDX body of a /resources post stored as a Markdown file.
 */
export const MarkdownContentRenderer = ({
  source,
}: MarkdownContentRendererProps) => {
  return <MDXRemote {...source} components={markdownComponents} />
}
